import React from 'react';
import { 
  Camera, 
  Brain, 
  Shield, 
  Activity, 
  Zap,
  MapPin,
  Clock,
  TrendingUp,
  Database,
  Eye,
  Lock
} from 'lucide-react';

const Metodika = () => {
  return (
    <div className="metodika-container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '12px',
        padding: '2rem',
        color: 'white',
        marginBottom: '2rem'
      }}>
        <h1 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem' }}>
          Metodika monitorovania dopravy
        </h1>
        <p style={{ fontSize: '1.1rem', opacity: 0.9 }}>
          Automatizovaný systém detekcie a klasifikácie vozidiel využívajúci hlboké učenie
        </p>
      </div>

      {/* Overview */}
      <div className="card" style={{ marginBottom: '2rem', padding: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Activity size={24} style={{ color: 'var(--primary)' }} />
          Úvod do systému
        </h2>
        <p style={{ lineHeight: '1.7', color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
          Tento monitorovací systém predstavuje pokročilé riešenie na kvantifikáciu dopravného toku 
          v mestskom prostredí s využitím moderných metód strojového učenia a počítačového videnia. 
          Systém je nasadený na adrese <strong>Na Revíne 29</strong> a poskytuje kontinuálny monitoring 
          vozidiel prechádzajúcich sledovaným úsekom.
        </p>
        <p style={{ lineHeight: '1.7', color: 'var(--color-text-secondary)' }}>
          Primárnym cieľom je automatizovaná kvantifikácia dopravnej intenzity s presnou 
          identifikáciou jedinečných vozidiel, analýzou smerovej distribúcie a časovou segmentáciou 
          detekovaných údajov v plnom súlade s európskou legislatívou o ochrane osobných údajov (GDPR).
        </p>
      </div>

      {/* Technical Stack */}
      <div className="card" style={{ marginBottom: '2rem', padding: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Brain size={24} style={{ color: 'var(--primary)' }} />
          Technologický stack
        </h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
          {/* Detection Model */}
          <div style={{ 
            padding: '1.5rem', 
            background: 'var(--color-surface-subtle)', 
            borderRadius: '8px',
            borderLeft: '4px solid var(--primary)' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <Brain size={20} style={{ color: 'var(--primary)' }} />
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600' }}>Detekčný model</h3>
            </div>
            <p style={{ color: 'var(--color-text-secondary)', lineHeight: '1.6', fontSize: '0.95rem' }}>
              <strong>YOLOv8 Large</strong> – konvolučná neurónová sieť optimalizovaná na 
              detekciu objektov v reálnom čase. Model dosahuje vysokú presnosť (mAP &gt; 0,89) pri 
              zachovaní efektívnej rýchlosti inferencie.
            </p>
          </div>

          {/* Hardware */}
          <div style={{ 
            padding: '1.5rem', 
            background: 'var(--color-surface-subtle)', 
            borderRadius: '8px',
            borderLeft: '4px solid var(--success)' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <Camera size={20} style={{ color: 'var(--success)' }} />
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600' }}>Senzorika</h3>
            </div>
            <p style={{ color: 'var(--color-text-secondary)', lineHeight: '1.6', fontSize: '0.95rem' }}>
              <strong>2K kamera</strong> (2560×1440 px) poskytuje vysoké rozlíšenie pre 
              presnú detekciu a sledovanie objektov. Statická konfigurácia eliminuje 
              potrebu dynamickej kalibrácie.
            </p>
          </div>

          {/* Location */}
          <div style={{ 
            padding: '1.5rem', 
            background: 'var(--color-surface-subtle)', 
            borderRadius: '8px',
            borderLeft: '4px solid var(--warning)' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <MapPin size={20} style={{ color: 'var(--warning)' }} />
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600' }}>Lokalizácia</h3>
            </div>
            <p style={{ color: 'var(--color-text-secondary)', lineHeight: '1.6', fontSize: '0.95rem' }}>
              <strong>Na Revíne 29</strong> – monitorovací bod strategicky umiestnený 
              pre optimálny zorný uhol na cestnú komunikáciu. 
            </p>
          </div>
        </div>
      </div>

      {/* Methodology */}
      <div className="card" style={{ marginBottom: '2rem', padding: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Zap size={24} style={{ color: 'var(--primary)' }} />
          Metodológia spracovania
        </h2>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--color-text-primary)' }}>
            1. Detekcia objektov
          </h3>
          <p style={{ lineHeight: '1.7', color: 'var(--color-text-secondary)', marginBottom: '0.75rem' }}>
            Videoprenos z kamery je spracúvaný po jednotlivých snímkach. 
            Model YOLOv8 Large aplikuje konvolučné vrstvy na extrakciu príznakov a následne 
            klasifikuje detegované oblasti do relevantných tried objektov.
          </p>
          <div style={{ 
            background: 'var(--color-surface-muted)', 
            padding: '1rem', 
            borderRadius: '6px', 
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            color: 'var(--color-text-primary)'
          }}>
            Model: YOLOv8l → Vstup: 2560×1440 px → Výstup: ohraničujúce rámčeky + pravdepodobnosti tried
          </div>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--color-text-primary)' }}>
            2. Sledovanie a identifikácia
          </h3>
          <p style={{ lineHeight: '1.7', color: 'var(--color-text-secondary)', marginBottom: '0.75rem' }}>
            Implementovaný sledovací algoritmus využíva metriku <strong>IoU (Intersection over Union)</strong> 
            na priraďovanie detegovaných objektov medzi po sebe nasledujúcimi snímkami. Každému vozidlu je 
            pridelený jedinečný identifikátor (tracking ID), ktorý umožňuje sledovanie trajektórie 
            objektu počas celého pobytu v zornom poli kamery.
          </p>
          <ul style={{ 
            lineHeight: '1.8', 
            color: 'var(--color-text-secondary)', 
            paddingLeft: '1.5rem',
            listStyleType: 'disc' 
          }}>
            <li>Kalmanov filter na predikciu polohy objektu</li>
            <li>Maďarský algoritmus (Hungarian algorithm) na optimálne priradenie detekčných párov</li>
            <li>Eliminuje duplicity – jedno vozidlo = jeden záznam</li>
          </ul>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--color-text-primary)' }}>
            3. Smerová analýza
          </h3>
          <p style={{ lineHeight: '1.7', color: 'var(--color-text-secondary)', marginBottom: '0.75rem' }}>
            Systém analyzuje pohybový vektor každého vozidla na základe zmien centroidov ohraničujúcich rámčekov 
            v čase. Smer je klasifikovaný do dvoch hlavných kategórií:
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={{ 
              padding: '1rem', 
              background: 'rgba(45, 212, 191, 0.12)', 
              borderRadius: '6px',
              border: '1px solid rgba(45, 212, 191, 0.4)'
            }}>
              <strong style={{ color: 'var(--color-text-primary)' }}>Z Vlárskej</strong>
              <p style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}>
                Vozidlá pohybujúce sa zľava doprava v referenčnom rámci kamery
              </p>
            </div>
            <div style={{ 
              padding: '1rem', 
              background: 'rgba(251, 191, 36, 0.16)', 
              borderRadius: '6px',
              border: '1px solid rgba(251, 191, 36, 0.45)'
            }}>
              <strong style={{ color: 'var(--color-text-primary)' }}>Z Na Revíne</strong>
              <p style={{ fontSize: '0.9rem', color: 'var(--color-text-primary)', marginTop: '0.5rem' }}>
                Vozidlá pohybujúce sa sprava doľava v referenčnom rámci kamery
              </p>
            </div>
          </div>
        </div>

        <div>
          <h3 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--color-text-primary)' }}>
            4. Časová segmentácia a uchovávanie
          </h3>
          <p style={{ lineHeight: '1.7', color: 'var(--color-text-secondary)' }}>
            Každá detekcia je doplnená o presnú časovú pečiatku (formát ISO 8601) a uložená 
            do relačnej databázy. Údaje sú segmentované podľa:
          </p>
          <ul style={{ 
            lineHeight: '1.8', 
            color: 'var(--color-text-secondary)', 
            paddingLeft: '1.5rem',
            listStyleType: 'disc',
            marginTop: '0.5rem' 
          }}>
            <li>Hodinových časových blokov (00:00–23:59)</li>
            <li>dennej/týždennej/mesačnej agregácie</li>
            <li>typu dňa (pracovný deň/víkend)</li>
          </ul>
        </div>
      </div>

      {/* GDPR Compliance */}
      <div className="card" style={{ marginBottom: '2rem', padding: '2rem', borderLeft: '4px solid var(--success)' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Shield size={24} style={{ color: 'var(--success)' }} />
          GDPR a ochrana súkromia
        </h2>
        
        <div style={{ 
          background: 'rgba(56, 161, 105, 0.12)', 
          padding: '1.5rem', 
          borderRadius: '8px',
          marginBottom: '1rem',
          border: '1px solid rgba(74, 222, 128, 0.45)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <Lock size={20} style={{ color: 'var(--success)' }} />
            <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--color-text-primary)' }}>
              Prístup Privacy by Design
            </h3>
          </div>
          <p style={{ lineHeight: '1.7', color: 'var(--color-text-secondary)' }}>
            Systém je navrhnutý s <strong>maximálnou ochranou súkromia od základu</strong>. 
            Nedochádza k uchovávaniu vizuálnych dát, čím je vylúčená možnosť identifikácie 
            fyzických osôb alebo evidenčných čísiel vozidiel.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          <div style={{ padding: '1rem', background: 'var(--color-surface-subtle)', borderRadius: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <Eye size={18} style={{ color: 'var(--primary)' }} />
              <strong style={{ fontSize: '0.95rem' }}>Žiadny záznam obrazu</strong>
            </div>
            <p style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
              Videoprenos je spracúvaný v reálnom čase v pamäti, bez ukladania snímok
            </p>
          </div>

          <div style={{ padding: '1rem', background: 'var(--color-surface-subtle)', borderRadius: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <Database size={18} style={{ color: 'var(--primary)' }} />
              <strong style={{ fontSize: '0.95rem' }}>Anonymizované údaje</strong>
            </div>
            <p style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
              Ukladajú sa výhradne agregované štatistiky bez identifikátorov
            </p>
          </div>

          <div style={{ padding: '1rem', background: 'var(--color-surface-subtle)', borderRadius: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <TrendingUp size={18} style={{ color: 'var(--primary)' }} />
              <strong style={{ fontSize: '0.95rem' }}>Iba štatistické účely</strong>
            </div>
            <p style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
              Analýza dát je orientovaná na dopravné trendy, nie na individuálne sledovanie
            </p>
          </div>
        </div>
      </div>

      {/* Data Output */}
      <div className="card" style={{ marginBottom: '2rem', padding: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Database size={24} style={{ color: 'var(--primary)' }} />
          Výstupné údaje a metriky
        </h2>
        
        <p style={{ lineHeight: '1.7', color: 'var(--color-text-secondary)', marginBottom: '1.5rem' }}>
          Systém poskytuje nasledujúce kvantifikovateľné metriky na analýzu dopravného toku:
        </p>

        <div style={{ 
          background: 'var(--color-surface-subtle)', 
          padding: '1.5rem', 
          borderRadius: '8px',
          border: '1px solid var(--color-border)' 
        }}>
          <table style={{ width: '100%', fontSize: '0.95rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                <th style={{ textAlign: 'left', padding: '0.75rem', color: 'var(--color-text-primary)' }}>Metrika</th>
                <th style={{ textAlign: 'left', padding: '0.75rem', color: 'var(--color-text-primary)' }}>Popis</th>
                <th style={{ textAlign: 'left', padding: '0.75rem', color: 'var(--color-text-primary)' }}>Formát</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={{ padding: '0.75rem', color: 'var(--color-text-secondary)' }}><strong>Unique ID</strong></td>
                <td style={{ padding: '0.75rem', color: 'var(--color-text-secondary)' }}>Jedinečný identifikátor vozidla</td>
                <td style={{ padding: '0.75rem', color: 'var(--color-text-secondary)', fontFamily: 'monospace' }}>UUID</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={{ padding: '0.75rem', color: 'var(--color-text-secondary)' }}><strong>Timestamp</strong></td>
                <td style={{ padding: '0.75rem', color: 'var(--color-text-secondary)' }}>Presný čas detekcie</td>
                <td style={{ padding: '0.75rem', color: 'var(--color-text-secondary)', fontFamily: 'monospace' }}>ISO 8601</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={{ padding: '0.75rem', color: 'var(--color-text-secondary)' }}><strong>Direction</strong></td>
                <td style={{ padding: '0.75rem', color: 'var(--color-text-secondary)' }}>Smer pohybu (z Vlárskej/z Na Revíne)</td>
                <td style={{ padding: '0.75rem', color: 'var(--color-text-secondary)', fontFamily: 'monospace' }}>Enum</td>
              </tr>
              <tr>
                <td style={{ padding: '0.75rem', color: 'var(--color-text-secondary)' }}><strong>Confidence</strong></td>
                <td style={{ padding: '0.75rem', color: 'var(--color-text-secondary)' }}>Pravdepodobnosť detekcie (práh &gt; 0,5)</td>
                <td style={{ padding: '0.75rem', color: 'var(--color-text-secondary)', fontFamily: 'monospace' }}>Reálne číslo [0–1]</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Applications */}
      <div className="card" style={{ padding: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <TrendingUp size={24} style={{ color: 'var(--primary)' }} />
          Aplikácie a využitie
        </h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
          <div style={{ 
            padding: '1.25rem', 
            background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)', 
            borderRadius: '8px',
            border: '1px solid rgba(99, 102, 241, 0.25)'
          }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--color-text-primary)' }}>
              Dopravné plánovanie
            </h3>
            <p style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                Analýza dopravných vzorcov na optimalizáciu infraštruktúry a časovania svetelnej signalizácie
            </p>
          </div>

          <div style={{ 
            padding: '1.25rem', 
            background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)', 
            borderRadius: '8px',
            border: '1px solid rgba(99, 102, 241, 0.25)'
          }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--color-text-primary)' }}>
              Environmentálne štúdie
            </h3>
            <p style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                Kvantifikácia dopravnej záťaže na výpočet emisií a hodnotenie vplyvu na ovzdušie
            </p>
          </div>

          <div style={{ 
            padding: '1.25rem', 
            background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)', 
            borderRadius: '8px',
            border: '1px solid rgba(99, 102, 241, 0.25)'
          }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--color-text-primary)' }}>
              Štatistická analýza
            </h3>
            <p style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
                Dlhodobý monitoring na identifikáciu trendov, sezónnych variácií a anomálií v dopravnom toku
            </p>
          </div>
        </div>
      </div>

      {/* Footer note */}
      <div style={{ 
        marginTop: '2rem', 
        padding: '1rem', 
        background: 'var(--color-surface-muted)', 
        borderRadius: '8px',
        fontSize: '0.9rem',
        color: 'var(--color-text-secondary)',
        textAlign: 'center',
        borderTop: '3px solid var(--primary)'
      }}>
        <Clock size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
        Systém pracuje 24/7 v automatickom režime s kontinuálnou aktualizáciou údajov
      </div>
    </div>
  );
};

export default Metodika;
